import api from 'api';

import { useMemo, useState } from 'react';
import { TableLayout } from 'lib/components/TableLayout';
import { Alert } from 'lib/components/Alert';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { ColumnButton } from 'lib/components/ColumnButton';
import Card from 'lib/components/Card/Card';
import { capitalize } from 'lodash';
import { Badge } from 'lib/components/Badge';
import {
  ReportsPaginationReportRow,
  ReportsPubIssuesTableDeadline
} from 'lib/types/reports/PaginationReportsRows';
import { DeadlineBadge } from 'routes/pagination/DeadlineBadge';
import TabGroup, { TabOption } from 'lib/components/Tabs';
import { getFirebaseContext } from 'utils/firebase';
import { logAndCaptureException } from 'utils';
import { useReportsLoading } from '../context/LoadingContext';
import { useReportParams } from '../context/ReportParamsContext';
import { fetchPublicationIssuesRowsForReports } from '../utils/elastic';
import { ReportHeader } from '../components/ReportHeader';
import PublicationIssuesTableFilterDialog, {
  PublicationIssuesTableFilter
} from './PublicationIssuesTableFilterDialog';

const DEFAULT_TIMEZONE = 'America/New_York';

const SEARCH_TABS: TabOption<ReportsPubIssuesTableDeadline>[] = [
  {
    id: ReportsPubIssuesTableDeadline.PublicationDate,
    label: 'Search by Publication Date',
    enabled: true
  },
  {
    id: ReportsPubIssuesTableDeadline.AdDeadline,
    label: 'Search by Ad Deadline',
    enabled: true
  }
];

export function PaginationTab() {
  const { getOrgIds, getDateRange } = useReportParams();
  const { dateRange, startDate, endDate } = getDateRange();
  const { withLoading } = useReportsLoading();

  const orgIds = getOrgIds();
  const [activeSearchTab, setActiveSearchTab] = useState<
    TabOption<ReportsPubIssuesTableDeadline>
  >(SEARCH_TABS[0]);

  const {
    value: paginationReportData,
    isLoading: isLoadingPaginationReport,
    error: paginationReportError
  } = useAsyncEffect<ReportsPaginationReportRow[]>({
    fetchData: async () =>
      await withLoading(
        fetchPublicationIssuesRowsForReports(
          orgIds,
          getDateRange,
          activeSearchTab.id
        )
      ),

    initialData: null,
    dependencies: [
      orgIds.sort().join(','),
      dateRange,
      startDate,
      endDate,
      activeSearchTab.id
    ],
    errorConfig: {
      service: ColumnService.ELASTIC,
      message: '[COLUMN REPORTS] Failed to fetch pagination report data',
      tags: {
        orgsFilter: orgIds.join(', '),
        dateRange,
        startDateFilter: startDate || '',
        endDateFilter: endDate || '',
        searchType: activeSearchTab.id
      }
    }
  });

  const rows: ReportsPaginationReportRow[] = paginationReportData || [];

  const [filter, setFilter] = useState<PublicationIssuesTableFilter>({
    status: '',
    medium: '',
    product: ''
  });
  const [editedFilter, setEditedFilter] =
    useState<PublicationIssuesTableFilter>(filter);
  const [search, setSearch] = useState('');

  const filteredRows: ReportsPaginationReportRow[] = useMemo(() => {
    return rows.filter((row: ReportsPaginationReportRow) => {
      const matchesStatus = !filter.status || row.status === filter.status;
      const matchesMedium = !filter.medium || row.medium === filter.medium;
      const matchesProduct = !filter.product || row.product === filter.product;
      const matchesSearch =
        !search || row.newspaper.toLowerCase().includes(search.toLowerCase());

      const matches =
        matchesStatus && matchesMedium && matchesProduct && matchesSearch;
      return matches;
    });
  }, [rows, filter, search]);

  const renderRow = (row: ReportsPaginationReportRow) => {
    const handleDownloadManifest = async () => {
      if (!row.publicationIssueId || !row.sectionId || !row.timezone) {
        logAndCaptureException(
          ColumnService.ELASTIC,
          new Error(
            'Missing required data for pagination issue download manifest'
          ),
          'Reports Pagination Tab export error',
          {
            publicationIssueId: row.publicationIssueId,
            sectionId: row.sectionId,
            timezone: row.timezone
          }
        );
        return;
      }

      const { response, error } = await api.safePost(
        'pagination/download-pagination-issue-manifest',
        {
          product: row.product,
          medium: row.medium,
          publicationIssueId: row.publicationIssueId,
          publicationIssueSectionId: row.sectionId,
          filingType: row.filingType
        }
      );

      if (error || !response?.url) {
        console.error('Failed to download manifest:', error);
        return;
      }

      window.open(response.url, '_blank');
    };

    return (
      <>
        <td className="px-2">{row.publicationDate}</td>
        <td className="px-2">
          {row.deadline ? (
            <DeadlineBadge
              deadline={getFirebaseContext().timestamp({
                seconds: Math.floor(row.deadline / 1000)
              })}
              timezone={row.timezone || DEFAULT_TIMEZONE}
            />
          ) : (
            ''
          )}{' '}
        </td>
        <td className="px-2">{row.newspaper}</td>
        <td className="px-2">
          <div>
            <Badge status="success" size="sm">
              {`${capitalize(row.status || '')}`}
            </Badge>
          </div>
        </td>
        <td className="px-2">
          <div>
            <Badge status="warning" size="sm">
              {`${capitalize(row.medium)}`}
            </Badge>
          </div>
        </td>
        <td className="px-2">
          <div>
            <Badge status="info" size="sm">
              {`${capitalize(row.product)}`}
            </Badge>
          </div>
        </td>
        <td className="px-2">{row.filingType || 'N/A'}</td>
        <td className="px-2">{row.ordersCount}</td>
        <td className="px-2">{row.totalColumnInches?.toFixed(2)}</td>
        <td className="px-2">
          <ColumnButton
            tertiary
            size="sm"
            onClick={handleDownloadManifest}
            startIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
            type="button"
            aria-label="Download CSV for publication issue"
            disabled={
              !row.ordersCount || !row.publicationIssueId || !row.sectionId
            }
          />
        </td>
      </>
    );
  };

  const tableColumns = [
    'Publication Date',
    'Ad Deadline',
    'Newspaper',
    'Status',
    'Medium',
    'Product',
    'Filing Type',
    'Count',
    'Total Column Inches',
    'Export CSV'
  ].map(column => {
    return (
      <th key={column} className="font-medium px-2">
        {column}
      </th>
    );
  });
  return (
    <Card>
      <ReportHeader
        title={'Pagination'}
        subtitle={`View the manifest and total column inches for each issue, sorted by ${
          activeSearchTab.id === ReportsPubIssuesTableDeadline.PublicationDate
            ? 'publication date'
            : 'ad deadline date'
        }`}
      />
      <TabGroup
        tabs={SEARCH_TABS}
        activeTab={activeSearchTab}
        onClickTab={setActiveSearchTab}
        id="pagination-search-tabs"
      />
      <TableLayout
        loading={isLoadingPaginationReport}
        data={filteredRows}
        columns={tableColumns}
        filterable={{
          shouldShowTableItem: (
            item: ReportsPaginationReportRow,
            search: string
          ) => item.newspaper.toLowerCase().includes(search.toLowerCase()),
          searchEnabled: true,
          onSearch: setSearch,
          additionalFilters: {
            applyFilterChanges: () => setFilter(editedFilter),
            resetFilters: () => {
              setFilter({ status: '', medium: '', product: '' });
              setEditedFilter({ status: '', medium: '', product: '' });
            },
            filterHasChanges:
              JSON.stringify(filter) !== JSON.stringify(editedFilter),
            numFiltersActive: Object.values(filter).filter(Boolean).length,
            onDialogOpen: () => setEditedFilter(filter),
            renderDialog: () => (
              <PublicationIssuesTableFilterDialog
                filter={editedFilter}
                setFilter={setEditedFilter}
              />
            )
          }
        }}
        header={{
          title: '',
          subtitle: ''
        }}
        renderRow={renderRow}
      />
      {paginationReportError && (
        <Alert status="error" id="pagination-tab-error">
          Error fetching pagination issues: {paginationReportError?.message}
        </Alert>
      )}
    </Card>
  );
}

export default PaginationTab;
